import React, { useState, useEffect, useCallback } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import Layout from "./components/Layout";
import Login from "./components/Login";
import Dashboard from "./components/Dashboard";
import Analytics from "./components/Analytics";
import KeywordTable from "./components/KeywordTable";
import SearchPerformance from "./components/SearchPerformance";
import ContentQueue from "./components/ContentQueue";
import ContentNew from "./components/ContentNew";
import ContentDetail from "./components/ContentDetail";
import ContentTemplates from "./components/ContentTemplates";
import PAADashboard from "./components/PAADashboard";
import CompetitorGap from "./components/CompetitorGap";
import LandingPages from "./components/LandingPages";
import TopicClusters from "./components/TopicClusters";
import InternalLinkBuilder from "./components/InternalLinkBuilder";
import ContentCalendar from "./components/ContentCalendar";
import BacklinkTracker from "./components/BacklinkTracker";
import FourOhFourManager from "./components/FourOhFourManager";
import ContentSyndication from "./components/ContentSyndication";
import SocialHub from "./components/SocialHub";
import GoogleBusiness from "./components/GoogleBusiness";
import LeadsCRM from "./components/LeadsCRM";
import Campaigns from "./components/Campaigns";
import LocalRankings from "./components/LocalRankings";
import RankTracker from "./components/RankTracker";
import BulkImport from "./components/BulkImport";
import SeoOptimizer from "./components/SeoOptimizer";
import SchemaManager from "./components/SchemaManager";
import AltTextGenerator from "./components/AltTextGenerator";
import ImageLibrary from "./components/ImageLibrary";
import AuditIssues from "./components/AuditIssues";
import WebVitals from "./components/WebVitals";
import AIAgent from "./components/AIAgent";
import AiInsights from "./components/AiInsights";
import GeoMonitor from "./components/GeoMonitor";
import Reports from "./components/Reports";
import ActivityLog from "./components/ActivityLog";
import PageDesigner from "./components/PageDesigner";
import WordPressSync from "./components/WordPressSync";
import SEOAuditPage from "./components/SEOAuditPage";
import SeoMaintenance from "./components/SeoMaintenance";
import SiteHealth from "./components/SiteHealth";
import WhiteLabel from "./components/WhiteLabel";
import SiteSettings from "./components/SiteSettings";
import Documentation from "./components/Documentation";
import SettingsPage from "./components/Settings";
import AdminDashboard from "./components/AdminDashboard";
import BehaviorOverview from "./components/BehaviorOverview";
import BehaviorPages from "./components/BehaviorPages";
import BehaviorPageDetail from "./components/BehaviorPageDetail";
import BehaviorSessions from "./components/BehaviorSessions";
import BehaviorReplay from "./components/BehaviorReplay";
import BehaviorExitIntent from "./components/BehaviorExitIntent";
import BehaviorTriggers from "./components/BehaviorTriggers";
import SocialMonitor from "./components/SocialMonitor";
import SocialMonitorPost from "./components/SocialMonitorPost";
import SocialMonitorBundles from "./components/SocialMonitorBundles";
import Automation from "./components/Automation";
import api, { onAuthFailure } from "./api/client";
import { isAgencyAdmin } from "./utils/subdomain";

export default function App() {
  const agencyMode = isAgencyAdmin();
  const [websites, setWebsites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(
    () => !!localStorage.getItem("access_token")
  );
  const [userInfo, setUserInfo] = useState(null);
  const navigate = useNavigate();

  const handleLogout = useCallback(() => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    setAuthenticated(false);
    setWebsites([]);
    setSelectedSite(null);
    setUserInfo(null);
    navigate("/login");
  }, [navigate]);

  useEffect(() => {
    onAuthFailure(handleLogout);
  }, [handleLogout]);

  // Pick up impersonation tokens from hash (admin Login As)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("impersonate=")) {
      const params = new URLSearchParams(hash.slice(1));
      const access = params.get("impersonate");
      const refresh = params.get("refresh");
      if (access && refresh) {
        localStorage.setItem("access_token", access);
        localStorage.setItem("refresh_token", refresh);
        window.location.hash = "";
        setAuthenticated(true);
        navigate("/");
      }
    }
  }, []);

  useEffect(() => {
    if (!authenticated) {
      setLoading(false);
      return;
    }
    setLoading(true);

    const mePromise = api
      .get("/auth/me/")
      .then((res) => {
        setUserInfo(res.data);
      })
      .catch(() => {
        setUserInfo({ user: { is_superuser: true }, plan: "agency", features: {}, usage: {} });
      });

    const sitesPromise = api
      .get("/websites/")
      .then((res) => {
        const sites = res.data.results || res.data;
        setWebsites(sites);
        if (sites.length > 0 && !selectedSite) {
          setSelectedSite(sites[0]);
        }
      })
      .catch(() => {
        setWebsites([]);
      });

    Promise.all([mePromise, sitesPromise]).finally(() => setLoading(false));
  }, [authenticated]);

  if (!authenticated) {
    return (
      <Routes>
        <Route
          path="/login"
          element={
            <Login
              agencyMode={agencyMode}
              onLogin={() => {
                setAuthenticated(true);
                navigate("/");
              }}
            />
          }
        />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-dark">
        <div className="text-copper font-mono text-lg animate-pulse">
          Loading AutoSEO...
        </div>
      </div>
    );
  }

  // Block non-superusers on admin subdomain
  if (agencyMode && !userInfo?.user?.is_superuser) {
    return (
      <div className="flex items-center justify-center h-screen bg-dark">
        <div className="text-center space-y-4">
          <div className="text-red-400 font-mono text-lg">Access Denied</div>
          <p className="text-gray-500 text-sm">Admin dashboard requires superuser access.</p>
          <button onClick={handleLogout} className="text-copper hover:text-copper/80 text-sm underline">
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <Layout
      websites={websites}
      selectedSite={selectedSite}
      onSelectSite={setSelectedSite}
      onLogout={handleLogout}
      userInfo={userInfo}
    >
      <Routes>
        <Route path="/" element={agencyMode ? <Navigate to="/admin-dashboard" replace /> : <Dashboard selectedSite={selectedSite} />} />
        <Route path="/analytics" element={<Analytics selectedSite={selectedSite} />} />
        <Route path="/keywords" element={<KeywordTable selectedSite={selectedSite} />} />
        <Route path="/search" element={<SearchPerformance selectedSite={selectedSite} />} />
        <Route path="/content" element={<ContentQueue selectedSite={selectedSite} />} />
        <Route path="/content/new" element={<ContentNew selectedSite={selectedSite} />} />
        <Route path="/content/templates" element={<ContentTemplates websites={websites} />} />
        <Route path="/content/:id" element={<ContentDetail selectedSite={selectedSite} />} />
        <Route path="/paa" element={<PAADashboard selectedSite={selectedSite} />} />
        <Route path="/competitors" element={<CompetitorGap selectedSite={selectedSite} />} />
        <Route path="/pages" element={<LandingPages selectedSite={selectedSite} />} />
        <Route path="/topic-clusters" element={<TopicClusters selectedSite={selectedSite} />} />
        <Route path="/internal-links" element={<InternalLinkBuilder selectedSite={selectedSite} />} />
        <Route path="/calendar" element={<ContentCalendar selectedSite={selectedSite} />} />
        <Route path="/backlinks" element={<BacklinkTracker selectedSite={selectedSite} />} />
        <Route path="/404-manager" element={<FourOhFourManager selectedSite={selectedSite} />} />
        <Route path="/syndication" element={<ContentSyndication selectedSite={selectedSite} />} />
        <Route path="/social" element={<SocialHub selectedSite={selectedSite} />} />
        <Route path="/social-monitor" element={<SocialMonitor />} />
        <Route path="/social-monitor/post/:id" element={<SocialMonitorPost />} />
        <Route path="/social-monitor/bundle/:id" element={<SocialMonitorBundles />} />
        <Route path="/google-business" element={<GoogleBusiness selectedSite={selectedSite} />} />
        <Route path="/leads" element={<LeadsCRM selectedSite={selectedSite} />} />
        <Route path="/campaigns" element={<Campaigns selectedSite={selectedSite} />} />
        <Route path="/local-rankings" element={<LocalRankings selectedSite={selectedSite} />} />
        <Route path="/rank-tracker" element={<RankTracker selectedSite={selectedSite} />} />
        <Route path="/bulk-import" element={<BulkImport selectedSite={selectedSite} />} />
        <Route path="/seo-optimizer" element={<SeoOptimizer selectedSite={selectedSite} />} />
        <Route path="/schema" element={<SchemaManager selectedSite={selectedSite} />} />
        <Route path="/alt-text" element={<AltTextGenerator selectedSite={selectedSite} />} />
        <Route path="/image-library" element={<ImageLibrary selectedSite={selectedSite} />} />
        <Route path="/behavior" element={<BehaviorOverview selectedSite={selectedSite} />} />
        <Route path="/behavior/pages" element={<BehaviorPages selectedSite={selectedSite} />} />
        <Route path="/behavior/page/:path" element={<BehaviorPageDetail selectedSite={selectedSite} />} />
        <Route path="/behavior/sessions" element={<BehaviorSessions selectedSite={selectedSite} />} />
        <Route path="/behavior/replay/:id" element={<BehaviorReplay selectedSite={selectedSite} />} />
        <Route path="/behavior/exit-intent" element={<BehaviorExitIntent selectedSite={selectedSite} />} />
        <Route path="/behavior/triggers" element={<BehaviorTriggers selectedSite={selectedSite} />} />
        <Route path="/audit" element={<AuditIssues selectedSite={selectedSite} />} />
        <Route path="/vitals" element={<WebVitals selectedSite={selectedSite} />} />
        <Route path="/ai-agent" element={<AIAgent selectedSite={selectedSite} />} />
        <Route path="/ai-insights" element={<AiInsights selectedSite={selectedSite} />} />
        <Route path="/geo" element={<GeoMonitor selectedSite={selectedSite} />} />
        <Route path="/reports" element={<Reports selectedSite={selectedSite} />} />
        <Route path="/activity-log" element={<ActivityLog selectedSite={selectedSite} />} />
        <Route path="/page-designer" element={<PageDesigner selectedSite={selectedSite} />} />
        <Route path="/wordpress-sync" element={<WordPressSync selectedSite={selectedSite} />} />
        <Route path="/seo-audit" element={<SEOAuditPage />} />
        <Route path="/seo-maintenance" element={<SeoMaintenance selectedSite={selectedSite} />} />
        <Route path="/site-health" element={<SiteHealth selectedSite={selectedSite} />} />
        <Route path="/white-label" element={<WhiteLabel />} />
        <Route path="/site-settings" element={<SiteSettings selectedSite={selectedSite} />} />
        <Route path="/docs" element={<Documentation />} />
        <Route path="/automation" element={<Automation selectedSite={selectedSite} />} />
        <Route path="/settings" element={<SettingsPage websites={websites} />} />
        <Route path="/admin-dashboard" element={<AdminDashboard />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}
